import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Switch } from '@/components/ui/switch'
import { getJSON, patchJSON } from '@/lib/api'

type Ceiling = 'hidden' | 'members' | 'public'
type Access = 'member_edit' | 'view_only' | 'staff_only'
type ContactField = {
  id: number
  key: string
  label: string
  field_access: Access
  exposure_ceiling: Ceiling
}
type CustomField = {
  id: number
  label: string
  field_type: string
  field_access: string
  exposure_ceiling: Ceiling
  filterable: boolean
}
type DirectoryField = ContactField & {
  kind: 'built-in' | 'custom'
  filterable?: boolean
  choice: boolean
}
type FieldsResponse = { contact_fields: ContactField[] }
type CustomFieldsResponse = { custom_fields: CustomField[] }

const ceilingLabels: Record<Ceiling, string> = {
  hidden: 'Hidden',
  members: 'Members',
  public: 'Public',
}
const accessLabels: Record<Access, string> = {
  member_edit: 'Member editable',
  view_only: 'View-only',
  staff_only: 'Staff only',
}

function normalizeAccess(value: string): Access {
  if (value === 'members may edit') return 'member_edit'
  if (value === 'view-only') return 'view_only'
  if (value === 'staff-only') return 'staff_only'
  return value as Access
}

function isChoice(type: string) {
  return type === 'single-select' || type === 'multi-select'
}

function CeilingControl({
  field,
  onChange,
}: {
  field: DirectoryField
  onChange: (value: Ceiling) => void
}) {
  if (field.key === 'email' || field.key === 'phone')
    return (
      <div>
        <p className="text-body-sm text-foreground">{ceilingLabels[field.exposure_ceiling]}</p>
        <p className="text-caption text-muted-foreground">Never public</p>
      </div>
    )
  if (field.field_access === 'staff_only')
    return (
      <div>
        <p className="text-body-sm text-foreground">Hidden</p>
        <p className="text-caption text-muted-foreground">Staff-only field</p>
      </div>
    )
  return (
    <Select value={field.exposure_ceiling} onValueChange={(value) => onChange(value as Ceiling)}>
      <SelectTrigger className="h-9 w-32" aria-label={`Exposure Ceiling for ${field.label}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(ceilingLabels).map(([value, label]) => (
          <SelectItem key={value} value={value}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function FieldsTable({
  fields,
  onUpdate,
}: {
  fields: DirectoryField[]
  onUpdate: (field: DirectoryField, changes: Record<string, unknown>) => void
}) {
  const [query, setQuery] = React.useState('')
  const [accessFilter, setAccessFilter] = React.useState<Set<string>>(new Set())
  const [sortKey, setSortKey] = React.useState<'field' | 'access' | 'ceiling'>('field')
  const [ascending, setAscending] = React.useState(true)
  const rows = fields
    .filter((field) => field.label.toLowerCase().includes(query.trim().toLowerCase()))
    .filter((field) => accessFilter.size === 0 || accessFilter.has(field.field_access))
    .sort((a, b) => {
      const left =
        sortKey === 'field'
          ? a.label
          : sortKey === 'access'
            ? accessLabels[a.field_access]
            : a.exposure_ceiling
      const right =
        sortKey === 'field'
          ? b.label
          : sortKey === 'access'
            ? accessLabels[b.field_access]
            : b.exposure_ceiling
      return (ascending ? 1 : -1) * left.localeCompare(right)
    })
  const toggleSort = (key: 'field' | 'access' | 'ceiling') =>
    sortKey === key ? setAscending((value) => !value) : (setSortKey(key), setAscending(true))
  const direction = ascending ? 'ascending' : 'descending'
  return (
    <section id="fields" className="overflow-hidden rounded-md border border-border bg-card">
      <h2 className="border-b border-border px-4 py-3 text-body font-medium text-foreground">
        Fields
      </h2>
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <Input
          className="h-9 w-72"
          placeholder="Search fields…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <FilterDropdown
          label="Field access"
          emptyLabel="Field access"
          options={Object.entries(accessLabels).map(([value, label]) => ({ value, label }))}
          selected={accessFilter}
          onChange={setAccessFilter}
        />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead
              active={sortKey === 'field'}
              direction={direction}
              onToggle={() => toggleSort('field')}
            >
              Field
            </SortableTableHead>
            <SortableTableHead
              active={sortKey === 'access'}
              direction={direction}
              onToggle={() => toggleSort('access')}
            >
              Field access
            </SortableTableHead>
            <SortableTableHead
              active={sortKey === 'ceiling'}
              direction={direction}
              onToggle={() => toggleSort('ceiling')}
            >
              Exposure ceiling
            </SortableTableHead>
            <TableHead>Filterable</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((field) => (
            <TableRow key={`${field.kind}-${field.id}`}>
              <TableCell className="text-body-sm font-medium">{field.label}</TableCell>
              <TableCell className="text-body-sm text-muted-foreground">
                {accessLabels[field.field_access]}
              </TableCell>
              <TableCell>
                <CeilingControl
                  field={field}
                  onChange={(value) => onUpdate(field, { exposure_ceiling: value })}
                />
              </TableCell>
              <TableCell>
                {field.choice ? (
                  <Switch
                    checked={field.filterable}
                    aria-label={`Filterable ${field.label}`}
                    onCheckedChange={(checked) => onUpdate(field, { filterable: checked })}
                  />
                ) : (
                  <span className="text-caption text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  )
}

export function AdminDirectoryFieldsPanel() {
  const queryClient = useQueryClient()
  const contactFields = useQuery({
    queryKey: ['association-contact-fields'],
    queryFn: () => getJSON<FieldsResponse>('/api/v1/contact_fields'),
    retry: false,
  })
  const customFields = useQuery({
    queryKey: ['custom-fields'],
    queryFn: () => getJSON<CustomFieldsResponse>('/api/v1/custom_fields'),
    retry: false,
  })
  const updateField = useMutation({
    mutationFn: ({ field, changes }: { field: DirectoryField; changes: Record<string, unknown> }) =>
      patchJSON(
        field.kind === 'built-in'
          ? `/api/v1/contact_fields/${field.id}`
          : `/api/v1/custom_fields/${field.id}`,
        { [field.kind === 'built-in' ? 'contact_field' : 'custom_field']: changes },
      ),
    onSuccess: (_data, { field }) =>
      queryClient.invalidateQueries({
        queryKey: [field.kind === 'built-in' ? 'association-contact-fields' : 'custom-fields'],
      }),
  })
  if (contactFields.isLoading || customFields.isLoading)
    return (
      <p className="text-body-sm text-muted-foreground" role="status">
        Loading directory fields...
      </p>
    )
  if (contactFields.isError || customFields.isError)
    return <p className="text-body-sm text-destructive">Directory fields unavailable</p>
  const fields: DirectoryField[] = [
    ...(contactFields.data?.contact_fields ?? []).map((field) => ({
      ...field,
      kind: 'built-in' as const,
      choice: false,
    })),
    ...(customFields.data?.custom_fields ?? []).map((field) => ({
      ...field,
      key: `custom-${field.id}`,
      field_access: normalizeAccess(field.field_access),
      kind: 'custom' as const,
      choice: isChoice(field.field_type),
    })),
  ]
  return (
    <FieldsTable
      fields={fields}
      onUpdate={(field, changes) => updateField.mutate({ field, changes })}
    />
  )
}
